// src/pages/client/ClientCandidateReportPage.jsx
// Individual candidate interview report - client version

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function ClientCandidateReportPage({ tenantKeyFromSubdomain }) {
  const navigate = useNavigate();
  const { tenantKey: tenantKeyFromUrl, interviewId } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  const videoRef = useRef(null);
  
  const [tenant, setTenant] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  // Generate correct paths based on routing mode
  const getPath = (path) => {
    if (isSubdomain) {
      return `/admin${path}`;
    } else {
      return `/client/${tenantKey}${path}`;
    }
  };

  useEffect(() => {
    fetchReport();
    loadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, tenantKey]);

  const loadTenant = async () => {
    if (!tenantKey) return;
    try {
      const response = await fetch(`${API_BASE}/tenants/${tenantKey}`);
      if (response.ok) {
        setTenant(await response.json());
      }
    } catch (err) {
      console.error("Failed to load tenant:", err);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await fetch(`${API_BASE}/scoring/${interviewId}`);
      if (!response.ok) {
        throw new Error('Failed to load report');
      }
      const data = await response.json();
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async (force = false) => {
    setAnalyzing(true);
    try {
      const url = force 
        ? `${API_BASE}/scoring/retry/${interviewId}`
        : `${API_BASE}/scoring/analyze/${interviewId}`;
      
      await fetch(url, { method: 'POST' });
      await fetchReport();
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const jumpToTime = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Apply tenant branding
  const branding = tenant?.branding || {};
  const primaryColor = branding.primaryColor || "#3ECF8E";

  const getScoreColor = (score) => {
    if (score >= 8) return '#22c55e';
    if (score >= 6) return '#3b82f6';
    if (score >= 4) return '#eab308';
    return '#ef4444';
  };

  const getRecommendationStyle = (rec) => {
    switch (rec) {
      case 'Strong Hire':
        return { background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
      case 'Hire':
        return { background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' };
      case 'Maybe':
        return { background: 'rgba(234, 179, 8, 0.2)', color: '#eab308' };
      case 'No Hire':
        return { background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
      default:
        return { background: 'rgba(255,255,255,0.1)', color: '#9ca3af' };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(getPath('/reports'))} style={styles.backButton}>
            ← Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const { interview, analysis, has_analysis } = report || {};
  const questions = analysis?.questions || [];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button 
          onClick={() => interview?.job_id 
            ? navigate(getPath(`/jobs/${interview.job_id}/report`))
            : navigate(getPath('/reports'))
          }
          style={styles.backLink}
        >
          ← Back to Job Report
        </button>
        <h1 style={styles.title}>
          {interview?.candidate_name || interview?.candidate_email || 'Candidate Report'}
        </h1>
        <p style={styles.subtitle}>
          {interview?.job_title || 'Interview Report'}
        </p>
      </div>

      <div style={styles.content}>
        {/* Not Analyzed State */}
        {!has_analysis && (
          <div style={styles.notAnalyzed}>
            <h2>Analysis Not Complete</h2>
            <p>
              {interview?.transcript 
                ? 'This interview has a transcript but hasn\'t been analyzed yet.'
                : 'This interview doesn\'t have a transcript yet.'}
            </p>
            {interview?.transcript && (
              <button 
                onClick={() => runAnalysis(false)}
                disabled={analyzing}
                style={{ ...styles.analyzeButton, background: primaryColor }}
              >
                {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
              </button>
            )}
          </div>
        )}

        {/* Analysis Results */}
        {has_analysis && (
          <>
            {/* Overall Score Card */}
            <div style={styles.overallCard}>
              <div style={styles.scoreSection}>
                <div 
                  style={{
                    ...styles.overallScore,
                    borderColor: getScoreColor(analysis?.overall_score),
                    color: getScoreColor(analysis?.overall_score)
                  }}
                >
                  {analysis?.overall_score?.toFixed(1)}
                </div>
                <div 
                  style={{
                    ...styles.recommendation,
                    ...getRecommendationStyle(analysis?.recommendation)
                  }}
                >
                  {analysis?.recommendation}
                </div>
              </div>
              <div style={styles.assessmentSection}>
                <h3 style={styles.assessmentTitle}>Overall Assessment</h3>
                <p style={styles.assessmentText}>{analysis?.overall_assessment}</p>
              </div>
            </div>

            {/* Strengths & Concerns */}
            <div style={styles.twoColumn}>
              <div style={styles.strengthsCard}>
                <h3 style={styles.cardTitle}>✓ Key Strengths</h3>
                <ul style={styles.list}>
                  {analysis?.key_strengths?.map((s, i) => (
                    <li key={i} style={styles.listItem}>{s}</li>
                  ))}
                </ul>
              </div>
              <div style={styles.concernsCard}>
                <h3 style={styles.cardTitle}>⚠ Key Concerns</h3>
                <ul style={styles.list}>
                  {analysis?.key_concerns?.map((c, i) => (
                    <li key={i} style={styles.listItem}>{c}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Video Player */}
            {interview?.recording_url && (
              <div style={styles.videoSection}>
                <h3 style={styles.sectionTitle}>Interview Recording</h3>
                <video 
                  ref={videoRef}
                  src={interview.recording_url}
                  controls
                  style={styles.video}
                />
              </div>
            )}

            {/* Question-by-Question Analysis */}
            <div style={styles.questionsSection}>
              <h3 style={styles.sectionTitle}>Question-by-Question Analysis</h3>
              {questions.map((q, i) => (
                <div key={i} style={styles.questionCard}>
                  <div style={styles.questionHeader}>
                    <span style={styles.questionNumber}>Q{q.question_number || i + 1}</span>
                    <span style={styles.questionCategory}>{q.question_category}</span>
                    <div 
                      style={{
                        ...styles.questionScore,
                        color: getScoreColor(q.score)
                      }}
                    >
                      {q.score}/10
                    </div>
                  </div>
                  
                  <div style={styles.questionText}>{q.question_text}</div>
                  
                  {q.question_rationale && (
                    <div style={styles.rationale}>
                      <strong>Why asked:</strong> {q.question_rationale}
                    </div>
                  )}
                  
                  <div style={styles.answerSection}>
                    <div style={styles.answerHeader}>
                      <strong>Answer Summary</strong>
                      {q.answer_start_time && (
                        <button 
                          onClick={() => jumpToTime(q.answer_start_time)}
                          style={{ ...styles.timestampButton, color: primaryColor }}
                        >
                          ▶ {formatTime(q.answer_start_time)}
                        </button>
                      )}
                    </div>
                    <p style={styles.answerText}>{q.answer_text}</p>
                  </div>
                  
                  <div style={styles.scoreRationale}>
                    <strong>Score Rationale:</strong> {q.score_rationale}
                  </div>
                  
                  {q.strengths?.length > 0 && (
                    <div style={styles.miniList}>
                      <strong style={{ color: '#22c55e' }}>Strengths:</strong>
                      {q.strengths.map((s, j) => (
                        <span key={j} style={styles.miniTag}>{s}</span>
                      ))}
                    </div>
                  )}
                  
                  {(q.areas_for_improvement?.length > 0 || q.weaknesses?.length > 0) && (
                    <div style={styles.miniList}>
                      <strong style={{ color: '#eab308' }}>Areas for Improvement:</strong>
                      {(q.areas_for_improvement || q.weaknesses || []).map((a, j) => (
                        <span key={j} style={styles.miniTagWarning}>{a}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Full Transcript */}
            <div style={styles.transcriptSection}>
              <button 
                onClick={() => setShowTranscript(!showTranscript)}
                style={styles.transcriptToggle}
              >
                {showTranscript ? '▼ Hide Full Transcript' : '▶ Show Full Transcript'}
              </button>
              {showTranscript && (
                <div style={styles.transcriptContent}>
                  <pre style={styles.transcriptText}>
                    {interview?.transcript || 'No transcript available'}
                  </pre>
                </div>
              )}
            </div>

            {/* Re-analyze Button */}
            <div style={styles.reanalyzeSection}>
              <button 
                onClick={() => runAnalysis(true)}
                disabled={analyzing}
                style={styles.reanalyzeButton}
              >
                {analyzing ? 'Analyzing...' : 'Re-run Analysis'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#9ca3af'
  },
  error: {
    padding: 40,
    textAlign: 'center',
    color: '#ef4444'
  },
  header: {
    padding: '24px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 12,
    padding: 0
  },
  backButton: {
    marginTop: 16,
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer'
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 8px 0',
    color: '#fff'
  },
  subtitle: {
    fontSize: 16,
    color: '#9ca3af',
    margin: 0
  },
  content: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 40px'
  },
  notAnalyzed: {
    textAlign: 'center',
    padding: 60,
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.1)'
  },
  analyzeButton: {
    marginTop: 20,
    padding: '14px 28px',
    border: 'none',
    borderRadius: 8,
    color: '#11181C',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 15
  },
  overallCard: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    gap: 30,
    padding: 30,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    marginBottom: 24
  },
  scoreSection: {
    textAlign: 'center'
  },
  overallScore: {
    width: 100,
    height: 100,
    borderRadius: '50%',
    border: '4px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    fontWeight: 700,
    margin: '0 auto 16px'
  },
  recommendation: {
    padding: '8px 20px',
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 600,
    display: 'inline-block'
  },
  assessmentSection: {
    
  },
  assessmentTitle: {
    fontSize: 16,
    color: '#9ca3af',
    marginBottom: 12
  },
  assessmentText: {
    fontSize: 16,
    lineHeight: 1.6,
    color: '#e5e7eb'
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginBottom: 32
  },
  strengthsCard: {
    padding: 24,
    background: 'rgba(34, 197, 94, 0.05)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    borderRadius: 12
  },
  concernsCard: {
    padding: 24,
    background: 'rgba(234, 179, 8, 0.05)',
    border: '1px solid rgba(234, 179, 8, 0.2)',
    borderRadius: 12
  },
  cardTitle: {
    fontSize: 14,
    marginBottom: 16,
    color: '#fff'
  },
  list: {
    margin: 0,
    paddingLeft: 20
  },
  listItem: {
    marginBottom: 8,
    color: '#e5e7eb',
    lineHeight: 1.5
  },
  videoSection: {
    marginBottom: 32
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 16
  },
  video: {
    width: '100%',
    borderRadius: 12,
    background: '#000'
  },
  questionsSection: {
    marginBottom: 32
  },
  questionCard: {
    padding: 24,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    marginBottom: 16
  },
  questionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12
  },
  questionNumber: {
    background: 'rgba(255,255,255,0.1)',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600
  },
  questionCategory: {
    background: 'rgba(59, 130, 246, 0.2)',
    color: '#3b82f6',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12
  },
  questionScore: {
    marginLeft: 'auto',
    fontSize: 18,
    fontWeight: 700
  },
  questionText: {
    fontSize: 16,
    fontWeight: 500,
    color: '#fff',
    marginBottom: 12
  },
  rationale: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 16,
    fontStyle: 'italic'
  },
  answerSection: {
    background: 'rgba(255,255,255,0.03)',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16
  },
  answerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },
  timestampButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500
  },
  answerText: {
    color: '#e5e7eb',
    lineHeight: 1.6,
    margin: 0
  },
  scoreRationale: {
    fontSize: 14,
    color: '#9ca3af',
    marginBottom: 12
  },
  miniList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    marginTop: 8
  },
  miniTag: {
    background: 'rgba(34, 197, 94, 0.1)',
    color: '#22c55e',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12
  },
  miniTagWarning: {
    background: 'rgba(234, 179, 8, 0.1)',
    color: '#eab308',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12
  },
  transcriptSection: {
    marginBottom: 32
  },
  transcriptToggle: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#9ca3af',
    padding: '12px 20px',
    borderRadius: 8,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontSize: 14
  },
  transcriptContent: {
    marginTop: 16,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: 20,
    maxHeight: 400,
    overflow: 'auto'
  },
  transcriptText: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#9ca3af',
    whiteSpace: 'pre-wrap',
    margin: 0
  },
  reanalyzeSection: {
    textAlign: 'center',
    paddingTop: 20,
    borderTop: '1px solid rgba(255,255,255,0.1)'
  },
  reanalyzeButton: {
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6,
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: 14
  }
};
